/**
 * React component for unit actions menu.
 * Displays context-sensitive actions for selected units.
 */

import { useUnitSelectionStore, useCommandModeStore, useBuildModeStore } from '@shared/stores';
import { UnitType } from '@shared/constants/units';
import ActionButton from './ActionButton';
import BuildingActions from './BuildingActions';

interface UnitActionsMenuProps {
  scene: unknown | null;
}

export default function UnitActionsMenu({ scene }: UnitActionsMenuProps) {
  const selectedUnitIds = useUnitSelectionStore((state) => state.selectedUnitIds);
  const { mode: commandMode, setMode: setCommandMode } = useCommandModeStore();
  const { isBuildMenuOpen, openBuildMenu } = useBuildModeStore();

  // Get unit types from selected units
  const getSelectedUnitTypes = (): UnitType[] => {
    if (!scene || typeof scene !== 'object' || scene === null || selectedUnitIds.length === 0) {
      return [];
    }

    const sceneWithMethods = scene as {
      getUnitManager?: () => {
        getUnit: (id: string) =>
          | {
              getUnitType: () => UnitType;
            }
          | undefined;
      } | null;
    };

    const unitManager = sceneWithMethods.getUnitManager?.();
    if (!unitManager) return [];

    const unitTypes: UnitType[] = [];
    for (const unitId of selectedUnitIds) {
      const unit = unitManager.getUnit(unitId);
      if (unit) {
        unitTypes.push(unit.getUnitType());
      }
    }
    return unitTypes;
  };

  const unitTypes = getSelectedUnitTypes();
  const hasBuilder = unitTypes.includes(UnitType.BUILDER);
  const isSingleUnit = selectedUnitIds.length === 1;

  // Don't show actions if no units selected
  if (selectedUnitIds.length === 0) {
    return null;
  }

  const handleStop = () => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getUnitCommandHandler?: () => {
        executeStopCommand?: (units: unknown[]) => void;
      } | null;
      getUnitManager?: () => {
        getUnit: (id: string) => unknown | undefined;
      } | null;
    };

    const commandHandler = sceneWithMethods.getUnitCommandHandler?.();
    const unitManager = sceneWithMethods.getUnitManager?.();
    if (!commandHandler || !unitManager) return;

    const units = selectedUnitIds
      .map((id) => unitManager.getUnit(id))
      .filter((unit) => unit !== undefined);

    if (commandHandler.executeStopCommand && units.length > 0) {
      commandHandler.executeStopCommand(units);
    }
  };

  const handleHoldPosition = () => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getUnitCommandHandler?: () => {
        executeHoldPositionCommand?: (units: unknown[]) => void;
      } | null;
      getUnitManager?: () => {
        getUnit: (id: string) => unknown | undefined;
      } | null;
    };

    const commandHandler = sceneWithMethods.getUnitCommandHandler?.();
    const unitManager = sceneWithMethods.getUnitManager?.();
    if (!commandHandler || !unitManager) return;

    const units = selectedUnitIds
      .map((id) => unitManager.getUnit(id))
      .filter((unit) => unit !== undefined);

    if (commandHandler.executeHoldPositionCommand && units.length > 0) {
      commandHandler.executeHoldPositionCommand(units);
    }
  };

  const handleAttackMove = () => {
    // Set attack move mode - next right-click will be attack move
    setCommandMode('attack-move');
  };

  const handleMove = () => {
    // Set move mode - next right-click will be regular move
    setCommandMode('move');
  };

  return (
    <div className="flex flex-col gap-3" data-action-panel>
      {/* Hide unit actions when build menu is open */}
      {!isBuildMenuOpen && (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Move"
            hotkey="M"
            onClick={handleMove}
            tooltip="Move to location (M, then right-click)"
            active={commandMode === 'move'}
          />
          <ActionButton
            label="Attack Move"
            hotkey="A"
            onClick={handleAttackMove}
            tooltip="Move and attack enemies in range (A, then right-click)"
            active={commandMode === 'attack-move'}
          />
          <ActionButton
            label="Stop"
            hotkey="S"
            onClick={handleStop}
            tooltip="Stop current action (S)"
          />
          <ActionButton
            label="Hold Position"
            hotkey="H"
            onClick={handleHoldPosition}
            tooltip="Hold position and attack in range (H)"
          />
        </div>
      )}
      {/* Show build menu button when Builder is selected and build menu is closed */}
      {hasBuilder && isSingleUnit && !isBuildMenuOpen && (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Build"
            hotkey="B"
            onClick={openBuildMenu}
            tooltip="Open build menu (B)"
          />
        </div>
      )}
      {/* Show building actions when Builder unit is selected (single unit only) */}
      {hasBuilder && isSingleUnit && <BuildingActions scene={scene} />}
    </div>
  );
}
